"use client";

import {
  ArrowRightCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import type { ReactNode } from "react";
import { toast as sonnerToast } from "sonner";
import { DEFAULT_TOASTER_ID } from "@/constants";
import type { ToasterId } from "@/types";

type ToastId = string;

export type ToastParams = {
  id?: ToastId;
  toasterId?: ToasterId;
  description?: string | ReactNode;
  duration?: number;
};

type ToastAPI = {
  success: (title: string, params?: ToastParams) => void;
  error: (title: string, params?: ToastParams) => void;
  warning: (title: string, params?: ToastParams) => void;
  info: (title: string, params?: ToastParams) => void;
  loading: (title: string, params?: ToastParams) => string | number;
  progress: (title: string, params?: ToastParams) => string | number;
  dismiss: (id?: ToastId) => void;
};

export const toast: ToastAPI = {
  success(title, { toasterId, ...rest } = {}) {
    sonnerToast.success(title, {
      ...rest,
      icon: <CheckCircleIcon className="text-ui-toast-success-icon" />,
      toasterId: toasterId ?? DEFAULT_TOASTER_ID,
    });
  },
  error(title, { toasterId, ...rest } = {}) {
    sonnerToast.error(title, {
      ...rest,
      icon: <XCircleIcon className="text-ui-toast-error-icon" />,
      toasterId: toasterId ?? DEFAULT_TOASTER_ID,
    });
  },
  warning(title, { toasterId, ...rest } = {}) {
    sonnerToast.warning(title, {
      ...rest,
      icon: <ExclamationTriangleIcon className="text-ui-toast-warning-icon" />,
      toasterId: toasterId ?? DEFAULT_TOASTER_ID,
    });
  },
  info(title, { toasterId, ...rest } = {}) {
    sonnerToast.message(title, {
      ...rest,
      icon: <ArrowRightCircleIcon className="text-ui-toast-info-icon" />,
      toasterId: toasterId ?? DEFAULT_TOASTER_ID,
    });
  },
  loading(title, { toasterId, ...rest } = {}) {
    return sonnerToast.loading(title, {
      ...rest,
      duration: Infinity,
      toasterId: toasterId ?? DEFAULT_TOASTER_ID,
    });
  },
  progress(title, { toasterId, ...rest } = {}) {
    let p = 0;
    const toastId = sonnerToast("0%", {
      ...rest,
      toasterId: toasterId ?? DEFAULT_TOASTER_ID,
    });
    const interval = setInterval(() => {
      p += 5;
      sonnerToast.loading(`${p}%`, {
        id: toastId,
        toasterId: toasterId ?? DEFAULT_TOASTER_ID,
      });
      if (p >= 100) {
        clearInterval(interval);
        sonnerToast.success(title, {
          id: toastId,
          icon: <CheckCircleIcon className="text-ui-toast-success-icon" />,
          toasterId: toasterId ?? DEFAULT_TOASTER_ID,
        });
      }
    }, 150);
    return toastId;
  },
  dismiss(id) {
    sonnerToast.dismiss(id);
  },
};
